import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Copy, Link, BarChart3, ArrowUpDown, UserCheck } from 'lucide-react';
import { FilterDropdown } from '../../components/ui/FilterDropdown';
import { CouponStatsSlideOver } from './CouponStatsSlideOver';
import { couponsApi } from '../../api/endpoints/coupons';
import { productsApi } from '../../api/endpoints/products';
import { categoriesApi } from '../../api/endpoints/categories';
import { DataTable, type Column } from '../../components/ui/DataTable';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { DateFilterBar } from '../../components/ui/DateFilterBar';
import { Textarea } from '../../components/ui/Textarea';
import { Toggle } from '../../components/ui/Toggle';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { PermissionGate } from '../../components/ui/PermissionGate';
import { DropdownMenu } from '../../components/ui/DropdownMenu';
import { useToast } from '../../components/ui/Alert';
import { usePagination } from '../../hooks/usePagination';
import { formatDate, formatCurrency } from '../../lib/formatters';
import { useStore } from '../../contexts/StoreContext';
import type { Coupon } from '../../types/coupon';

export function CouponList() {
  const { storeId } = useParams<{ storeId: string }>();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentStore } = useStore();
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const { page, limit, goToPage } = usePagination({
    resetDeps: [sortDir, startDate, endDate],
  });

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState<Coupon | null>(null);
  const [statsTargetId, setStatsTargetId] = useState<string | null>(null);

  // Form state
  const [formCode, setFormCode] = useState('');
  const [formType, setFormType] = useState<'percentage' | 'fixed'>('percentage');
  const [formAmount, setFormAmount] = useState<number>(0);
  const [formDescription, setFormDescription] = useState('');
  const [formMinPurchase, setFormMinPurchase] = useState<number>(0);
  const [formMaxUses, setFormMaxUses] = useState<number>(0);
  const [formUsesPerCustomer, setFormUsesPerCustomer] = useState<number>(0);
  const [formMaximumAmount, setFormMaximumAmount] = useState<number>(0);
  const [formStartDate, setFormStartDate] = useState('');
  const [formExpiryDate, setFormExpiryDate] = useState('');
  const [formIsActive, setFormIsActive] = useState(true);
  // Affiliate
  const [formAffName, setFormAffName] = useState('');
  const [formAffEmail, setFormAffEmail] = useState('');
  const [formAffCommType, setFormAffCommType] = useState<'percentage' | 'fixed'>('percentage');
  const [formAffCommAmount, setFormAffCommAmount] = useState<number>(0);
  const [formAffMaxCommission, setFormAffMaxCommission] = useState<number>(0);
  const [showAffiliate, setShowAffiliate] = useState(false);
  // Product/category filter
  const [showProductFilter, setShowProductFilter] = useState(false);
  const [filterMode, setFilterMode] = useState<'applicable' | 'excluded'>('applicable');
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const { data: couponDetail } = useQuery({
    queryKey: ['coupon-detail', storeId, editTarget?._id],
    queryFn: () => couponsApi.get(storeId!, editTarget!._id),
    enabled: !!storeId && !!editTarget,
  });

  const { data: productsData } = useQuery({
    queryKey: ['products-mini', storeId],
    queryFn: () => productsApi.list(storeId!, { limit: 200 }),
    enabled: !!storeId && showModal && showProductFilter,
  });

  const { data: categoriesData } = useQuery({
    queryKey: ['categories-mini', storeId],
    queryFn: () => categoriesApi.list(storeId!),
    enabled: !!storeId && showModal && showProductFilter,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['coupons', storeId, { page, limit, sort: sortDir, startDate, endDate }],
    queryFn: () => couponsApi.list(storeId!, {
      page,
      limit,
      sort: sortDir !== 'desc' ? sortDir : undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    }),
    enabled: !!storeId,
  });

  const archiveMutation = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      couponsApi.archive(storeId!, id, archived),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons', storeId] });
      toast('success', t('common.success'));
    },
    onError: () => toast('error', t('common.error')),
  });

  const createMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => couponsApi.create(storeId!, payload as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons', storeId] });
      toast('success', t('coupons.createSuccess'));
      closeModal();
    },
    onError: () => toast('error', t('common.error')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      couponsApi.update(storeId!, id, payload as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons', storeId] });
      toast('success', t('coupons.updateSuccess'));
      closeModal();
    },
    onError: () => toast('error', t('common.error')),
  });

  function openCreate() {
    setEditTarget(null);
    setFormCode(''); setFormType('percentage'); setFormAmount(0); setFormDescription('');
    setFormMinPurchase(0); setFormMaxUses(0); setFormUsesPerCustomer(0); setFormMaximumAmount(0);
    setFormStartDate(''); setFormExpiryDate(''); setFormIsActive(true);
    setFormAffName(''); setFormAffEmail(''); setFormAffCommType('percentage'); setFormAffCommAmount(0); setFormAffMaxCommission(0); setShowAffiliate(false);
    setShowProductFilter(false); setFilterMode('applicable'); setSelectedProducts([]); setSelectedCategories([]);
    setShowModal(true);
  }

  function openEdit(coupon: Coupon) {
    setEditTarget(coupon);
    setFormCode(coupon.couponId);
    setFormType(coupon.discountType);
    setFormAmount(coupon.amount ?? 0);
    setFormDescription(coupon.description ?? '');
    setFormMinPurchase(coupon.minPurchase ?? 0);
    setFormMaxUses(coupon.maxUses ?? 0);
    setFormUsesPerCustomer(coupon.usesPerCustomer ?? 0);
    setFormMaximumAmount(coupon.maximumAmount ?? 0);
    setFormStartDate(coupon.startDate?.split('T')[0] ?? '');
    setFormExpiryDate(coupon.expiryDate?.split('T')[0] ?? '');
    setFormIsActive(coupon.isActive);
    const aff = coupon.affiliator;
    if (aff?.name) {
      setShowAffiliate(true);
      setFormAffName(aff.name ?? '');
      setFormAffEmail(aff.email ?? '');
      setFormAffCommType(aff.commissionType ?? 'percentage');
      // Coupons created via the legacy /stores/coupons/create endpoint store
      // the commission as `amount` / `maximumAmount`; the new dashboard
      // endpoint mirrors both names. Accept either so editing an existing
      // affiliate coupon shows its real numbers instead of zero.
      setFormAffCommAmount(aff.commissionAmount ?? aff.amount ?? 0);
      setFormAffMaxCommission(aff.maxCommission ?? aff.maximumAmount ?? 0);
    } else {
      setShowAffiliate(false);
      setFormAffName(''); setFormAffEmail(''); setFormAffCommType('percentage'); setFormAffCommAmount(0); setFormAffMaxCommission(0);
    }
    // Detect filter mode from whichever list is populated. A coupon can be
    // either "applicable" (allow-list) or "excluded" (deny-list) but not
    // both; the form's single set of chips serves both modes.
    const applicable = (coupon.applicableProducts?.length ?? 0) + (coupon.applicableCategories?.length ?? 0);
    const excluded = (coupon.excludedProducts?.length ?? 0) + (coupon.excludedCategories?.length ?? 0);
    if (applicable > 0 || excluded > 0) {
      setShowProductFilter(true);
      if (excluded > 0 && applicable === 0) {
        setFilterMode('excluded');
        setSelectedProducts(coupon.excludedProducts ?? []);
        setSelectedCategories(coupon.excludedCategories ?? []);
      } else {
        setFilterMode('applicable');
        setSelectedProducts(coupon.applicableProducts ?? []);
        setSelectedCategories(coupon.applicableCategories ?? []);
      }
    } else {
      setShowProductFilter(false);
      setFilterMode('applicable');
      setSelectedProducts([]); setSelectedCategories([]);
    }
    setShowModal(true);
  }

  function closeModal() { setShowModal(false); setEditTarget(null); }

  function handleSave() {
    if (!formCode.trim() || formAmount <= 0) return;
    const payload: Record<string, unknown> = {
      couponId: formCode,
      discountType: formType,
      amount: formAmount,
      description: formDescription || undefined,
      minPurchase: formMinPurchase || undefined,
      maxUses: formMaxUses || undefined,
      usesPerCustomer: formUsesPerCustomer || undefined,
      maximumAmount: formType === 'percentage' && formMaximumAmount ? formMaximumAmount : undefined,
      startDate: formStartDate || undefined,
      expiryDate: formExpiryDate || undefined,
      isActive: formIsActive,
    };
    if (showAffiliate && formAffName.trim()) {
      payload.affiliator = {
        name: formAffName,
        email: formAffEmail || undefined,
        commissionType: formAffCommType,
        commissionAmount: formAffCommAmount,
        maxCommission: formAffCommType === 'percentage' && formAffMaxCommission ? formAffMaxCommission : undefined,
      };
    }
    if (showProductFilter) {
      if (filterMode === 'excluded') {
        if (selectedProducts.length) payload.excludedProducts = selectedProducts;
        if (selectedCategories.length) payload.excludedCategories = selectedCategories;
        payload.applicableProducts = [];
        payload.applicableCategories = [];
      } else {
        if (selectedProducts.length) payload.applicableProducts = selectedProducts;
        if (selectedCategories.length) payload.applicableCategories = selectedCategories;
        payload.excludedProducts = [];
        payload.excludedCategories = [];
      }
    } else {
      payload.applicableProducts = [];
      payload.applicableCategories = [];
      payload.excludedProducts = [];
      payload.excludedCategories = [];
    }
    if (editTarget) {
      const { couponId: _, ...rest } = payload;
      updateMutation.mutate({ id: editTarget._id, payload: rest });
    } else {
      createMutation.mutate(payload);
    }
  }

  const columns: Column<Coupon>[] = [
    {
      key: 'couponId',
      header: t('coupons.code'),
      sortable: true,
      render: (c) => {
        const storeDomain = currentStore?.domain || `${(currentStore as any)?.id}.00stores.com`;
        const link = `https://${storeDomain}?coupon=${encodeURIComponent(c.couponId)}`;
        const aff = c.affiliator;
        const hasAffiliate = !!aff?.name;
        return (
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <span className="font-mono font-medium text-accent">{c.couponId}</span>
              <button
                onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(link).catch(() => {}); toast('success', t('common.copied', 'Copied')); }}
                className="rounded p-1 text-text-muted hover:text-accent hover:bg-bg-hover transition-colors"
                title={t('coupons.copyLink', 'Copy Link')}
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
              {hasAffiliate && (() => {
                const hasAmount = aff?.commissionAmount != null && aff.commissionAmount > 0;
                const commissionLabel = hasAmount
                  ? (aff?.commissionType === 'percentage'
                      ? `${aff.commissionAmount}%`
                      : formatCurrency(aff!.commissionAmount!, currentStore?.currency))
                  : null;
                return (
                  <>
                    <Badge variant="info" className="gap-1">
                      <UserCheck className="h-3 w-3" />
                      {t('coupons.affiliateBadge', 'مسوّق')}
                      {commissionLabel && (
                        <>
                          <span className="opacity-50 mx-0.5">·</span>
                          <span className="font-semibold">{commissionLabel}</span>
                        </>
                      )}
                    </Badge>
                    <button
                      onClick={(e) => { e.stopPropagation(); setStatsTargetId(c._id); }}
                      className="rounded p-1 text-text-muted hover:text-accent hover:bg-bg-hover transition-colors"
                      title={t('coupons.viewStats', 'عرض الإحصائيات')}
                      aria-label={t('coupons.viewStats', 'عرض الإحصائيات')}
                    >
                      <BarChart3 className="h-3.5 w-3.5" />
                    </button>
                  </>
                );
              })()}
            </div>
            {hasAffiliate && (
              <span className="text-[11px] text-text-muted truncate" title={aff?.email || ''}>
                {aff?.name}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'discountType',
      header: t('common.type'),
      render: (c) => (
        <Badge variant={c.discountType === 'percentage' ? 'info' : 'accent'}>
          {c.discountType === 'percentage' ? `${c.amount}%` : formatCurrency(c.amount ?? 0, currentStore?.currency)}
        </Badge>
      ),
    },
    {
      key: 'usage',
      header: t('coupons.usage'),
      render: (c) => (
        <span className="text-text-secondary">
          {c.currentUses ?? 0}{c.maxUses ? ` / ${c.maxUses}` : ''}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (c) => (
        <Badge variant={c.isActive ? 'success' : 'default'}>
          {c.isActive ? t('coupons.active') : t('coupons.inactive')}
        </Badge>
      ),
    },
    {
      key: 'expiry',
      header: t('coupons.expiry'),
      render: (c) => (
        <span className="text-xs text-text-secondary">
          {c.expiryDate ? formatDate(c.expiryDate) : '-'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (c) => {
        const storeDomain = currentStore?.domain || `${(currentStore as any)?.id}.00stores.com`;
        const couponLink = `https://${storeDomain}?coupon=${encodeURIComponent(c.couponId)}`;
        const aff = c.affiliator;
        const affLink = (aff?.id || aff?.affiliatorId) ? `https://${storeDomain}/affiliator/${aff.id || aff.affiliatorId}` : null;

        return (
          <PermissionGate requires="coupons.edit">
            <DropdownMenu
              items={[
                { label: t('common.edit'), onClick: () => openEdit(c) },
                {
                  label: t('coupons.viewStats', 'View Stats'),
                  icon: <BarChart3 className="h-4 w-4" />,
                  onClick: () => setStatsTargetId(c._id),
                },
                {
                  label: t('coupons.copyLink', 'Copy Link'),
                  icon: <Copy className="h-4 w-4" />,
                  onClick: () => {
                    navigator.clipboard.writeText(couponLink).catch(() => {});
                    toast('success', t('common.copied', 'Copied'));
                  },
                },
                ...(affLink ? [{
                  label: t('coupons.copyAffiliateLink', 'Copy Affiliate Link'),
                  icon: <Link className="h-4 w-4" />,
                  onClick: () => {
                    navigator.clipboard.writeText(affLink).catch(() => {});
                    toast('success', t('common.copied', 'Copied'));
                  },
                }] : []),
                {
                  label: c.archived ? t('coupons.restore') : t('common.delete'),
                  icon: <Trash2 className="h-4 w-4" />,
                  onClick: () => archiveMutation.mutate({ id: c._id, archived: !c.archived }),
                },
              ]}
            />
          </PermissionGate>
        );
      },
    },
  ];

  const coupons = data?.coupons ?? [];
  const total = data?.pagination?.total ?? coupons.length;
  const totalPages = data?.pagination?.pages ?? Math.ceil(total / limit);

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold">{t('sidebar.coupons')}</h1>
            {total > 0 && (
              <span className="rounded-full bg-bg-tertiary px-2.5 py-0.5 text-xs font-medium text-text-secondary">
                {total}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-text-secondary">{t('coupons.description', 'Manage discount coupons')}</p>
        </div>
        <div className="flex items-center gap-2">
          <PermissionGate requires="coupons.edit">
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4" />
              {t('coupons.addCoupon')}
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <FilterDropdown
          icon={ArrowUpDown}
          options={[
            { value: 'desc', label: t('common.newestFirst', 'الأحدث أولاً') },
            { value: 'asc', label: t('common.oldestFirst', 'الأقدم أولاً') },
          ]}
          value={sortDir}
          onChange={(v) => setSortDir(v as 'desc' | 'asc')}
        />
        <DateFilterBar
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
        />
      </div>

      <div className="rounded-xl border border-border-default bg-bg-secondary shadow-sm overflow-hidden">
        <DataTable
          columns={columns}
          data={coupons}
          isLoading={isLoading}
          keyExtractor={(c) => c._id}
          emptyTitle={t('common.noResults')}
          page={page}
          totalPages={totalPages}
          total={total}
          limit={limit}
          onPageChange={goToPage}
        />
      </div>

      {/* Create / Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={closeModal}
        title={editTarget ? t('coupons.editCoupon') : t('coupons.addCoupon')}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={closeModal}>{t('common.cancel')}</Button>
            <Button
              onClick={handleSave}
              isLoading={createMutation.isPending || updateMutation.isPending}
              disabled={!formCode.trim() || formAmount <= 0}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          {/* Basic */}
          <Input label={t('coupons.code')} required disabled={!!editTarget} value={formCode} onChange={(e) => setFormCode(e.target.value)} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label={t('coupons.discountType')}
              options={[
                { value: 'percentage', label: t('coupons.percentage') },
                { value: 'fixed', label: t('coupons.fixedAmount') },
              ]}
              value={formType}
              onChange={(e) => setFormType(e.target.value as 'percentage' | 'fixed')}
            />
            <Input label={t('coupons.amount')} type="number" step="0.01" required value={formAmount || ''} onChange={(e) => setFormAmount(Number(e.target.value) || 0)} />
          </div>
          {formType === 'percentage' && (
            <div>
              <Input
                label={t('coupons.maximumAmount', 'Maximum Discount Amount')}
                type="number"
                step="0.01"
                min="0"
                value={formMaximumAmount || ''}
                onChange={(e) => setFormMaximumAmount(Number(e.target.value) || 0)}
              />
              <p className="mt-1 text-xs text-text-muted">
                {t('coupons.maximumAmountHint', 'Cap the maximum discount value for percentage coupons')}
              </p>
            </div>
          )}
          <Textarea id="desc" label={t('common.description')} rows={2} value={formDescription} onChange={(e) => setFormDescription(e.target.value)} />

          {/* Limits */}
          <div className="grid gap-4 sm:grid-cols-3">
            <Input label={t('coupons.minPurchase')} type="number" step="0.01" value={formMinPurchase || ''} onChange={(e) => setFormMinPurchase(Number(e.target.value) || 0)} />
            <Input label={t('coupons.maxUses')} type="number" value={formMaxUses || ''} onChange={(e) => setFormMaxUses(Number(e.target.value) || 0)} />
            <Input label={t('coupons.usesPerCustomer')} type="number" value={formUsesPerCustomer || ''} onChange={(e) => setFormUsesPerCustomer(Number(e.target.value) || 0)} />
          </div>

          {/* Dates */}
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label={t('coupons.startDate')} type="date" value={formStartDate} onChange={(e) => setFormStartDate(e.target.value)} />
            <Input label={t('coupons.expiryDate')} type="date" value={formExpiryDate} onChange={(e) => setFormExpiryDate(e.target.value)} />
          </div>

          <Toggle label={t('coupons.active')} checked={formIsActive} onChange={setFormIsActive} />

          {/* Affiliate */}
          <div className="border-t border-border-default pt-4">
            <Toggle
              label={t('coupons.enableAffiliate', 'Enable Affiliate')}
              checked={showAffiliate}
              onChange={setShowAffiliate}
            />
            {showAffiliate && (
              <div className="mt-3 space-y-3 rounded-lg border border-border-default/50 bg-bg-tertiary p-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input label={t('coupons.affiliateName', 'Affiliate Name')} value={formAffName} onChange={(e) => setFormAffName(e.target.value)} />
                  <Input label={t('coupons.affiliateEmail', 'Affiliate Email')} type="email" value={formAffEmail} onChange={(e) => setFormAffEmail(e.target.value)} placeholder="affiliate@example.com" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Select
                    label={t('coupons.commissionType', 'Commission Type')}
                    options={[
                      { value: 'percentage', label: t('coupons.percentage') },
                      { value: 'fixed', label: t('coupons.fixedAmount') },
                    ]}
                    value={formAffCommType}
                    onChange={(e) => setFormAffCommType(e.target.value as 'percentage' | 'fixed')}
                  />
                  <Input label={t('coupons.commissionAmount', 'Commission Amount')} type="number" step="0.01" value={formAffCommAmount || ''} onChange={(e) => setFormAffCommAmount(Number(e.target.value) || 0)} />
                </div>
                {formAffCommType === 'percentage' && (
                  <div>
                    <Input
                      label={t('coupons.maxCommission', 'Max Commission Amount')}
                      type="number"
                      step="0.01"
                      min="0"
                      value={formAffMaxCommission || ''}
                      onChange={(e) => setFormAffMaxCommission(Number(e.target.value) || 0)}
                    />
                    <p className="mt-1 text-xs text-text-muted">
                      {t('coupons.maxCommissionHint', 'Cap the maximum commission for percentage-based affiliate commissions')}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Product / Category Filters */}
          <div className="border-t border-border-default pt-4">
            <Toggle
              label={t('coupons.enableProductFilter', 'Restrict to specific products/categories')}
              checked={showProductFilter}
              onChange={setShowProductFilter}
            />
            {showProductFilter && (
              <div className="mt-3 space-y-3 rounded-lg border border-border-default/50 bg-bg-tertiary p-4">
                <Select
                  label={t('coupons.filterMode', 'Filter Mode')}
                  options={[
                    { value: 'applicable', label: t('coupons.applicableOnly', 'Only these products/categories') },
                    { value: 'excluded', label: t('coupons.excludedOnly', 'Exclude these products/categories') },
                  ]}
                  value={filterMode}
                  onChange={(e) => setFilterMode(e.target.value as 'applicable' | 'excluded')}
                />

                {/* Categories */}
                <div>
                  <label className="mb-1 block text-sm font-medium text-text-secondary">
                    {t('coupons.categories', 'Categories')}
                  </label>
                  <div className="flex flex-wrap gap-2 rounded-lg border border-border-default bg-bg-secondary p-2 min-h-[40px]">
                    {(categoriesData?.categories ?? []).map((cat) => {
                      const selected = selectedCategories.includes(cat._id);
                      return (
                        <button
                          key={cat._id}
                          type="button"
                          onClick={() => setSelectedCategories(prev =>
                            selected ? prev.filter(id => id !== cat._id) : [...prev, cat._id]
                          )}
                          className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                            selected
                              ? 'bg-accent text-white'
                              : 'bg-bg-tertiary text-text-secondary hover:bg-bg-hover'
                          }`}
                        >
                          {cat.name}
                        </button>
                      );
                    })}
                    {(categoriesData?.categories ?? []).length === 0 && (
                      <span className="text-xs text-text-muted">{t('common.noResults')}</span>
                    )}
                  </div>
                </div>

                {/* Products */}
                <div>
                  <label className="mb-1 block text-sm font-medium text-text-secondary">
                    {t('coupons.products', 'Products')}
                  </label>
                  <div className="flex flex-wrap gap-2 rounded-lg border border-border-default bg-bg-secondary p-2 max-h-48 overflow-y-auto min-h-[40px]">
                    {(productsData?.products ?? []).map((prod) => {
                      const selected = selectedProducts.includes(prod._id);
                      return (
                        <button
                          key={prod._id}
                          type="button"
                          onClick={() => setSelectedProducts(prev =>
                            selected ? prev.filter(id => id !== prod._id) : [...prev, prod._id]
                          )}
                          className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                            selected
                              ? 'bg-accent text-white'
                              : 'bg-bg-tertiary text-text-secondary hover:bg-bg-hover'
                          }`}
                        >
                          {prod.title}
                        </button>
                      );
                    })}
                    {(productsData?.products ?? []).length === 0 && (
                      <span className="text-xs text-text-muted">{t('common.noResults')}</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Usage History (edit mode only) */}
          {editTarget && couponDetail?.usage && couponDetail.usage.length > 0 && (
            <div className="border-t border-border-default pt-4">
              <h3 className="text-sm font-semibold mb-2">
                {t('coupons.usageHistory', 'Usage History')}
                <span className="text-text-muted font-normal ms-1">({couponDetail.stats?.totalUses ?? 0})</span>
              </h3>
              {couponDetail.stats?.totalDiscount != null && couponDetail.stats.totalDiscount > 0 && (
                <p className="text-xs text-text-muted mb-3">
                  {t('coupons.totalDiscount', 'Total discount given')}: {formatCurrency(couponDetail.stats.totalDiscount, currentStore?.currency)}
                </p>
              )}
              <div className="max-h-48 overflow-y-auto space-y-1.5">
                {couponDetail.usage.map((u: any) => (
                  <div key={u._id} className="flex items-center justify-between rounded-lg bg-bg-tertiary px-3 py-2 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-accent">#{u.order?.referenceId || '-'}</span>
                      {u.customer?.name && <span className="text-text-secondary">{u.customer.name}</span>}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-text-muted">{u.discountAmount ? formatCurrency(u.discountAmount, currentStore?.currency) : ''}</span>
                      <span className="text-text-muted">{u.createdAt ? formatDate(u.createdAt) : ''}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Stats slide-over */}
      <CouponStatsSlideOver
        isOpen={!!statsTargetId}
        onClose={() => setStatsTargetId(null)}
        couponId={statsTargetId}
        storeId={storeId!}
      />
    </div>
  );
}
